(() => {
  'use strict';

  const config = window.laSkateCatalogGuard;
  const hostname = window.location.hostname.toLowerCase();
  if (!config || (hostname !== 'laskate.com' && hostname !== 'www.laskate.com')) return;

  const entityDecoder = document.createElement('textarea');
  ['viewCart', 'blockedMessage'].forEach((key) => {
    entityDecoder.innerHTML = String(config[key] || '');
    config[key] = entityDecoder.value;
  });

  const body = document.body;
  const cartUrl = config.cartUrl || '/cart';
  const cartJsonUrl = `${String(cartUrl).replace(/\/$/, '')}.js`;
  const injectedMenuLinkSelector = '.la-header__qikify a[href], .tmenu_wrapper a[href], #qikify-tmenu a[href]';
  const storefrontHosts = new Set([
    'laskate.com',
    'www.laskate.com',
    'lbskate.com',
    'www.lbskate.com'
  ]);
  const checkoutControlSelector = [
    'button[name="checkout"]',
    'input[name="checkout"]',
    'a[href^="/checkout"]',
    'a[href*="/checkout?"]',
    '.rebuy-cart__checkout-button'
  ].join(',');

  let cartState = 'unknown';
  let scheduledCheck = 0;
  let scheduledUiSanitization = 0;

  function isRestrictedProduct(product) {
    const vendor = product?.vendor || product?.product?.vendor || '';
    const identity = [
      product?.title,
      product?.product_title,
      product?.handle,
      product?.product?.title,
      product?.product?.handle
    ].filter(Boolean).join(' ').toLowerCase();
    return String(vendor).trim().toLowerCase() === 'nike'
      || /(^|[^a-z0-9])nike([^a-z0-9]|$)/.test(identity);
  }

  function cartContainsRestrictedItem(cart) {
    return Array.isArray(cart?.items) && cart.items.some(isRestrictedProduct);
  }

  function updateCartCount(cart) {
    if (!Number.isFinite(Number(cart?.item_count))) return;
    document.querySelectorAll('[data-la-cart-count]').forEach((count) => {
      count.textContent = String(cart.item_count);
    });
  }

  function getGuardAlert() {
    let alert = document.getElementById('la-cart-guard-alert');
    if (alert) return alert;

    alert = document.createElement('div');
    alert.id = 'la-cart-guard-alert';
    alert.setAttribute('role', 'alert');
    alert.setAttribute('aria-live', 'assertive');
    alert.hidden = true;

    const message = document.createElement('span');
    message.setAttribute('data-la-cart-alert-message', '');
    alert.appendChild(message);

    const cartLink = document.createElement('a');
    cartLink.href = cartUrl;
    cartLink.textContent = config.viewCart || 'View cart';
    alert.appendChild(cartLink);

    body.appendChild(alert);
    return alert;
  }

  function showGuardAlert() {
    const alert = getGuardAlert();
    const message = alert.querySelector('[data-la-cart-alert-message]');
    if (message) message.textContent = config.blockedMessage;
    alert.hidden = false;
  }

  function hideGuardAlert() {
    const alert = document.getElementById('la-cart-guard-alert');
    if (alert) alert.hidden = true;
  }

  function syncCheckoutControls() {
    const shouldBlock = cartState === 'restricted';

    document.querySelectorAll(checkoutControlSelector).forEach((control) => {
      if (shouldBlock) {
        if ('disabled' in control && !control.disabled) {
          control.disabled = true;
          control.dataset.laGuardDisabled = 'true';
        }

        if (!control.hasAttribute('aria-disabled')) {
          control.dataset.laGuardAriaDisabled = 'true';
        }
        control.setAttribute('aria-disabled', 'true');
      } else {
        if (control.dataset.laGuardDisabled === 'true') {
          control.disabled = false;
          delete control.dataset.laGuardDisabled;
        }

        if (control.dataset.laGuardAriaDisabled === 'true') {
          control.removeAttribute('aria-disabled');
          delete control.dataset.laGuardAriaDisabled;
        }
      }
    });
  }

  function setCartFromData(cart, announce = false) {
    const restricted = cartContainsRestrictedItem(cart);
    cartState = restricted ? 'restricted' : 'clear';
    body.classList.toggle('laskate-cart-restricted', restricted);
    updateCartCount(cart);
    syncCheckoutControls();

    if (restricted && announce && !document.querySelector('[data-la-cart-guard-message]')) {
      showGuardAlert();
    } else if (!restricted) {
      hideGuardAlert();
    }

    return restricted;
  }

  async function checkCart(announce = false) {
    try {
      const response = await fetch(cartJsonUrl, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
        cache: 'no-store'
      });
      if (!response.ok) throw new Error(`Cart check failed: ${response.status}`);

      return setCartFromData(await response.json(), announce);
    } catch (error) {
      cartState = 'unknown';
      body.classList.remove('laskate-cart-restricted');
      syncCheckoutControls();
      return null;
    }
  }

  function scheduleCartCheck(announce = false) {
    window.clearTimeout(scheduledCheck);
    scheduledCheck = window.setTimeout(() => checkCart(announce), 120);
  }

  function isNikeIdentity(link) {
    const identity = `${link.textContent || ''} ${link.getAttribute('href') || ''}`.toLowerCase();
    return /(^|[^a-z0-9])nike([^a-z0-9]|$)/.test(identity);
  }

  function sanitizeInjectedMenu() {
    document.querySelectorAll(injectedMenuLinkSelector).forEach((link) => {
      if (isNikeIdentity(link)) {
        const menuItem = link.closest('li.tmenu_item, li, .tmenu_item, .tmenu_submenu_item');
        (menuItem || link).remove();
        return;
      }

      const href = link.getAttribute('href');
      try {
        const url = new URL(href, window.location.href);
        const linkHost = url.hostname.toLowerCase();
        const lbStoreHost = linkHost === 'lbskate.com' || linkHost === 'www.lbskate.com';
        if ((url.protocol === 'http:' || url.protocol === 'https:') && lbStoreHost) {
          const localHref = `${url.pathname}${url.search}${url.hash}`;
          if (href !== localHref) link.setAttribute('href', localHref);
        }
      } catch (error) {
        // Leave malformed or app-owned links untouched.
      }
    });
  }

  function routeInjectedMenuLink(event) {
    if (!(event.target instanceof Element)) return;
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const link = event.target.closest(injectedMenuLinkSelector);
    if (!link || link.hasAttribute('download')) return;

    const href = link.getAttribute('href');
    if (!href || href.startsWith('#') || /^javascript:/i.test(href)) return;

    try {
      const url = new URL(href, window.location.href);
      if (!['http:', 'https:'].includes(url.protocol)) return;
      if (!storefrontHosts.has(url.hostname.toLowerCase())) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      window.location.assign(`${url.pathname}${url.search}${url.hash}`);
    } catch (error) {
      // Leave malformed or app-owned links untouched.
    }
  }

  function scheduleInjectedUiSanitization() {
    if (scheduledUiSanitization) return;
    scheduledUiSanitization = window.requestAnimationFrame(() => {
      scheduledUiSanitization = 0;
      sanitizeInjectedMenu();
    });
  }

  function syncHeaderEnd() {
    const header = document.querySelector('.la-header');
    if (!header) return;
    document.documentElement.style.setProperty(
      '--header-end',
      `${header.getBoundingClientRect().bottom.toFixed(1)}px`
    );
  }

  function filterRebuyWidget(widget) {
    const products = widget?.data?.products;
    if (!Array.isArray(products)) return;

    const allowedProducts = products.filter((product) => !isRestrictedProduct(product));
    if (allowedProducts.length !== products.length) {
      widget.data.products = allowedProducts;
    }
  }

  function filterAllRebuyWidgets() {
    (window.Rebuy?.widgets || []).forEach(filterRebuyWidget);
  }

  function cartFromRebuyEvent(event) {
    const value = event.detail?.cart;
    if (Array.isArray(value?.items)) return value;
    if (Array.isArray(value?.cart?.items)) return value.cart;
    return null;
  }

  function knownRestrictedCart() {
    return cartState === 'restricted' || cartContainsRestrictedItem(window.Rebuy?.Cart?.cart);
  }

  function hideRestrictedSmartCart() {
    if (typeof window.Rebuy?.SmartCart?.hide === 'function') {
      window.Rebuy.SmartCart.hide();
    }
  }

  function blockRestrictedCheckout(event) {
    if (!knownRestrictedCart()) return false;

    event.preventDefault();
    event.stopImmediatePropagation();
    hideRestrictedSmartCart();
    showGuardAlert();
    if (window.location.pathname !== cartUrl) window.location.assign(cartUrl);
    return true;
  }

  document.addEventListener('click', (event) => {
    routeInjectedMenuLink(event);
  }, true);

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;
    if (!event.target.closest(checkoutControlSelector)) return;
    blockRestrictedCheckout(event);
  }, true);

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const submitter = event.submitter || document.activeElement;
    const isCheckoutSubmit = submitter instanceof Element && submitter.matches('[name="checkout"]');
    const postsToCheckout = String(form.action || '').includes('/checkout');
    if (isCheckoutSubmit || postsToCheckout) blockRestrictedCheckout(event);
  }, true);

  ['rebuy.ready', 'rebuy.productsChange', 'rebuy.refresh', 'rebuy.beforeShow']
    .forEach((eventName) => document.addEventListener(eventName, (event) => {
      filterRebuyWidget(event.detail?.widget);
      filterAllRebuyWidgets();
    }));

  document.addEventListener('rebuy.loaded', filterAllRebuyWidgets);

  ['rebuy:cart.ready', 'rebuy:cart.add', 'rebuy:cart.change'].forEach((eventName) => {
    document.addEventListener(eventName, (event) => {
      const cart = cartFromRebuyEvent(event);
      const announce = eventName !== 'rebuy:cart.ready';
      if (cart) setCartFromData(cart, announce);
      else scheduleCartCheck(announce);
    });
  });

  document.addEventListener('rebuy:smartcart.show', (event) => {
    const smartCart = event.detail?.smartcart?.cart || window.Rebuy?.Cart?.cart;
    if (smartCart) setCartFromData(smartCart, true);
    if (!knownRestrictedCart()) return;

    hideRestrictedSmartCart();
    showGuardAlert();
    if (window.location.pathname !== cartUrl) window.location.assign(cartUrl);
  });

  ['on:cart:add', 'on:line-item:change', 'dispatch:cart-drawer:refresh']
    .forEach((eventName) => document.addEventListener(eventName, () => scheduleCartCheck(true)));

  window.addEventListener('pageshow', () => scheduleCartCheck(false));
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') scheduleCartCheck(false);
  });

  const observer = new MutationObserver(scheduleInjectedUiSanitization);
  const laHeader = document.querySelector('.la-header');
  if (laHeader) {
    syncHeaderEnd();
    window.addEventListener('resize', syncHeaderEnd, { passive: true });
    window.addEventListener('load', syncHeaderEnd, { once: true });
    if ('ResizeObserver' in window) {
      window.laHeaderResizeObserver = new ResizeObserver(syncHeaderEnd);
      window.laHeaderResizeObserver.observe(laHeader);
    }
  }
  sanitizeInjectedMenu();
  filterAllRebuyWidgets();
  observer.observe(body, { childList: true, subtree: true, attributes: true, attributeFilter: ['href'] });
  checkCart(false);
})();
